import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Firebase services
import { getProjectById, updateProject } from '../../lib/firebase/firestore';

// Types
import { Project, Layout } from '../../types';

export const EditProjectPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Partial<Project>>({
    title: '',
    description: '',
    coverImageUrl: '',
    tags: [],
    year: new Date().getFullYear(),
    contentSections: [],
  });

  const [loading, setLoading] = useState(false);

  // Load project on mount
  useEffect(() => {
    const loadProject = async () => {
      if (!id) return;
      const data = await getProjectById(id);
      if (data) {
        setProject(data);
      }
    };
    loadProject();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProject((prev) => ({ ...prev, [name]: value }));
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tags = e.target.value.split(',').map((t) => t.trim());
    setProject((prev) => ({ ...prev, tags }));
  };

  // Handle section changes
  const handleSectionChange = (index: number, field: string, value: string) => {
    const updatedSections = [...(project.contentSections || [])];
    updatedSections[index] = { ...updatedSections[index], [field]: field === 'layout' ? (value as Layout) : value };
    setProject((prev) => ({ ...prev, contentSections: updatedSections }));
  };

  const addSection = () => {
    const newSection: { heading: string; text: string; imageUrl: string; layout: Layout } = {
      heading: "",
      text: "",
      imageUrl: "",
      layout: "full-width" as Layout,
    };
    setProject((prev) => ({
      ...prev,
      contentSections: [...(prev.contentSections || []), newSection],
    }));
  };

  const removeSection = (index: number) => {
    const updatedSections = (project.contentSections || []).filter((_, i) => i !== index);
    setProject((prev) => ({ ...prev, contentSections: updatedSections }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project.coverImageUrl) return alert('Please enter a cover image URL');

    setLoading(true);

    try {
      await updateProject(id!, {
        title: project.title || '',
        description: project.description || '',
        coverImageUrl: project.coverImageUrl || '',
        tags: project.tags || [],
        year: parseInt(project.year as unknown as string),
        contentSections: project.contentSections || [],
      });

      alert('Project updated successfully!');
      navigate('/admin/projects');
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project');
    }

    setLoading(false);
  };

  if (!project.title) return <div>Loading...</div>;

  return (
    <div style={{ padding: '2rem' }}>
      <h2>Edit Project</h2>
      <form onSubmit={handleSubmit}>
        {/* Basic Fields */}
        <input
          type="text"
          name="title"
          placeholder="Title"
          value={project.title || ''}
          onChange={handleChange}
          required
          style={{ width: '100%', marginBottom: '1rem', padding: '0.5rem' }}
        />

        <textarea
          name="description"
          placeholder="Description"
          value={project.description || ''}
          onChange={handleChange}
          required
          style={{ width: '100%', marginBottom: '1rem', padding: '0.5rem' }}
        />

        <input
          type="text"
          name="tags"
          placeholder="Tags (comma-separated)"
          value={(project.tags as string[])?.join(', ') || ''}
          onChange={handleTagChange}
          required
          style={{ width: '100%', marginBottom: '1rem', padding: '0.5rem' }}
        />

        <input
          type="number"
          name="year"
          placeholder="Year"
          value={project.year || ''}
          onChange={handleChange}
          required
          style={{ width: '100%', marginBottom: '1rem', padding: '0.5rem' }}
        />

        <input
          type="url"
          name="coverImageUrl"
          placeholder="Cover Image URL"
          value={project.coverImageUrl || ''}
          onChange={handleChange}
          required
          style={{ width: '100%', marginBottom: '1rem', padding: '0.5rem' }}
        />

        {/* Content Sections */}
        <h3>Content Sections</h3>
        {(project.contentSections || []).map((section, index) => (
          <div key={index} style={{ border: '1px solid #ccc', padding: '1rem', marginBottom: '1rem' }}>
            <input
              type="text"
              placeholder="Heading"
              value={section.heading || ''}
              onChange={(e) => handleSectionChange(index, 'heading', e.target.value)}
              style={{ width: '100%', marginBottom: '0.5rem' }}
            />
            <textarea
              placeholder="Text"
              value={section.text || ''}
              onChange={(e) => handleSectionChange(index, 'text', e.target.value)}
              style={{ width: '100%', marginBottom: '0.5rem' }}
            />
            <input
              type="url"
              placeholder="Image URL"
              value={section.imageUrl || ''}
              onChange={(e) => handleSectionChange(index, 'imageUrl', e.target.value)}
              style={{ width: '100%', marginBottom: '0.5rem' }}
            />
            <select
              value={section.layout || 'full-width'}
              onChange={(e) => handleSectionChange(index, 'layout', e.target.value)}
              style={{ width: '100%', marginBottom: '0.5rem' }}
            >
              <option value="text-left">Text Left</option>
              <option value="text-right">Text Right</option>
              <option value="full-width">Full Width</option>
            </select>

            <button
              type="button"
              onClick={() => removeSection(index)}
              style={{
                marginTop: '0.5rem',
                backgroundColor: '#ff4d4d',
                color: 'white',
                border: 'none',
                padding: '0.3rem 0.6rem',
                cursor: 'pointer',
              }}
            >
              Remove Section
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addSection}
          style={{
            marginBottom: '2rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
          }}
        >
          ➕ Add Section
        </button>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: '#000',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {loading ? 'Saving...' : 'Update Project'}
        </button>
      </form>
    </div>
  );
};